import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Tour, FilterState } from "@/types/tour";
import { supabase } from "@/integrations/supabase/client";
import { tourUrl, companyUrl } from "@/lib/slugify";
import { emptyFilters } from "@/types/tour";
import { getLowestPrice, getAttributeColor, getAggregatedReview, parseRating, getCheapestPlatformIds } from "@/lib/tourUtils";
import {
  Star,
  Check,
  X as XIcon,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Circle,
} from "lucide-react";
import {
  VIBE_OPTIONS,
  CROWD_SIZE_OPTIONS,
  BOARDING_OPTIONS,
  BEST_FOR_OPTIONS,
  AVAILABLE_TO_OPTIONS,
  RATING_ATTRS,
} from "@/types/tour";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

interface ComparisonTableProps {
  tours: Tour[];
  allTours: Tour[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

type ArrayFilterKey = Exclude<
  keyof FilterState,
  "price_min" | "price_max" | "review_min" | "duration_min" | "duration_max"
>;

// Helper: extract unique sorted values from tours for a given field
function deriveOptions(tours: Tour[], field: keyof Tour): string[] {
  const set = new Set<string>();
  tours.forEach(t => {
    const val = t[field];
    if (Array.isArray(val)) val.forEach(v => { if (v) set.add(v); });
    else if (typeof val === 'string' && val) set.add(val);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

const ATTR_FILTER_CONFIG: { key: ArrayFilterKey; label: string; mobileLabel?: string; options: string[] }[] = [
  { key: "vibe", label: "Vibe", options: VIBE_OPTIONS },
  { key: "crowd_size", label: "Crowd Size", options: CROWD_SIZE_OPTIONS },
  { key: "boarding", label: "Boarding", options: BOARDING_OPTIONS },
  { key: "best_for", label: "Best For", options: BEST_FOR_OPTIONS },
];

/** Renders 1-5 filled/empty circles */
function CircleRating({ value, extra }: { value: string | null; extra?: string | null }) {
  const rating = parseRating(value);
  if (rating === null && !extra) {
    if (value && value !== "") {
      // Special text value like "No alcohol"
      return <span className="text-xs text-muted-foreground">{value}</span>;
    }
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return (
    <div className="relative flex items-center justify-center">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Circle
            key={i}
            className={`w-3.5 h-3.5 ${i <= (rating || 0) ? "fill-primary text-primary" : "text-primary"}`}
          />
        ))}
      </div>
      {extra && (
        <span className="absolute left-full ml-2 text-[10px] text-muted-foreground whitespace-nowrap">{extra}</span>
      )}
    </div>
  );
}

/** Map legacy DB values for display */
function displayValue(value: string | null): string | null {
  if (value === 'Not included') return 'Not available';
  return value;
}

function AttributeCell({ value }: { value: string | null }) {
  const display = displayValue(value);
  if (!display) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${getAttributeColor(display)}`}>
      {display}
    </span>
  );
}

/* --- Shared cell wrappers --- */
const LABEL_W_PX_DESKTOP = 160;
const CARD_W_PX_DESKTOP = 300;
const LABEL_W_PX_MOBILE = 100;
const CARD_W_PX_MOBILE = 250;

function LabelCell({
  children,
  className = "",
  labelW,
}: {
  children: React.ReactNode;
  className?: string;
  labelW: number;
}) {
  return (
    <div
      className={`sticky left-0 z-20 bg-background px-1 md:px-4 py-2 md:py-3 flex items-start flex-shrink-0 self-stretch ${className}`}
      style={{ minWidth: labelW, width: labelW }}
    >
      {children}
    </div>
  );
}

function CardCell({
  children,
  isFirst = false,
  isLast = false,
  className = "",
  cardW,
}: {
  children: React.ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  className?: string;
  cardW: number;
}) {
  return (
    <div className="flex-shrink-0 px-1 md:px-2" style={{ minWidth: cardW, width: cardW }}>
      <div
        className={`bg-card border-x border-border ${isFirst ? "border-t rounded-t-xl" : ""} ${isLast ? "border-b rounded-b-xl" : ""} ${!isFirst && !isLast ? "border-b border-border" : isFirst ? "border-b border-border" : ""} px-3 md:px-5 py-3 flex items-center justify-center ${className}`}
      >
        {children}
      </div>
    </div>
  );
}

/* --- Filter label with dropdown --- */
function FilterableLabel({
  label,
  mobileLabel,
  filterKey,
  options,
  filters,
  onFiltersChange,
}: {
  label: string;
  mobileLabel?: string;
  filterKey: ArrayFilterKey;
  options: string[];
  filters: FilterState;
  onFiltersChange: (f: FilterState) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = filters[filterKey] as string[];

  const toggle = (value: string) => {
    const next = active.includes(value) ? active.filter((v) => v !== value) : [...active, value];
    onFiltersChange({ ...filters, [filterKey]: next });
  };

  return (
    <div className="w-full">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-0.5 md:gap-1.5 w-full">
        <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide text-left leading-tight">
          {mobileLabel ? (
            <>
              <span className="md:hidden whitespace-pre-line">{mobileLabel}</span>
              <span className="hidden md:inline">{label}</span>
            </>
          ) : (
            label
          )}
        </span>
        {active.length > 0 && (
          <span className="text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
            {active.length}
          </span>
        )}
        {open ? (
          <ChevronUp className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap gap-1 animate-fade-in">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                active.includes(opt)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:border-primary/50"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SliderLabel({
  label,
  value,
  range,
  onChange,
  indicator,
  noOption,
  noActive,
  onNoChange,
}: {
  label: string;
  value: number | null;
  range: { min: number; max: number };
  onChange: (v: number | null) => void;
  indicator?: string;
  noOption?: string;
  noActive?: boolean;
  onNoChange?: (checked: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasFilter = value !== null || noActive;
  return (
    <div className="w-full">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-0.5 md:gap-1.5 w-full">
        <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">{label}</span>
        {hasFilter && (
          <span className="text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
            !
          </span>
        )}
        {open ? (
          <ChevronUp className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="mt-3 space-y-2 animate-fade-in">
          <Slider
            min={range.min}
            max={range.max}
            step={1}
            value={[value ?? range.min]}
            onValueChange={([v]) => {
              if (!noActive) onChange(v <= range.min ? null : Math.round(v));
            }}
            className={noActive ? "opacity-30 pointer-events-none" : ""}
          />
          <div className="text-[10px] text-muted-foreground">
            Min: {value !== null && !noActive ? `${Math.round(value)}${indicator || ""}` : "Any"}
          </div>
          {noOption && onNoChange && (
            <button
              onClick={() => onNoChange(!noActive)}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                noActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:border-primary/50"
              }`}
            >
              {noOption}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DualSliderLabel({
  label,
  values,
  range,
  onChange,
  step = 5,
  format = (v: number) => `$${v}`,
}: {
  label: string;
  values: [number | null, number | null];
  range: { min: number; max: number };
  onChange: (min: number | null, max: number | null) => void;
  step?: number;
  format?: (v: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const sv: [number, number] = [values[0] ?? range.min, values[1] ?? range.max];
  return (
    <div className="w-full">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-0.5 md:gap-1.5 w-full">
        <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">{label}</span>
        {(values[0] !== null || values[1] !== null) && (
          <span className="text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
            !
          </span>
        )}
        {open ? (
          <ChevronUp className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground ml-auto flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="mt-3 space-y-2 animate-fade-in">
          <Slider
            min={range.min}
            max={range.max}
            step={step}
            value={sv}
            onValueChange={([min, max]) => onChange(min <= range.min ? null : min, max >= range.max ? null : max)}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{format(sv[0])}</span>
            <span>{format(sv[1])}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Main Component ---------- */
export function ComparisonTable({ tours, allTours, filters, onFiltersChange }: ComparisonTableProps) {
  const mobile = useIsMobile();

  const trackPlatformClick = useCallback((platformId: string) => {
    supabase.from('platform_clicks').insert({ platform_id: platformId }).then(() => {});
  }, []);
  const LABEL_W_PX = mobile ? LABEL_W_PX_MOBILE : LABEL_W_PX_DESKTOP;
  const CARD_W_PX = mobile ? CARD_W_PX_MOBILE : CARD_W_PX_DESKTOP;
  const CARD_STEP = CARD_W_PX;
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [inclusionsExpanded, setInclusionsExpanded] = useState(false);
  const [priceFilterOpen, setPriceFilterOpen] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [currentTourIdx, setCurrentTourIdx] = useState(0);
  const photoRowRef = useRef<HTMLDivElement>(null);

  const scrollToStart = useCallback(() => {
    headerScrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    bodyScrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  }, []);

  const handleFiltersChange = useCallback(
    (f: FilterState) => {
      onFiltersChange(f);
      // Defer scroll to after React re-renders with new tour list
      requestAnimationFrame(() => {
        scrollToStart();
      });
    },
    [onFiltersChange, scrollToStart],
  );

  const relevantInclusions = useMemo(() => {
    const all = new Set<string>();
    allTours.forEach((t) => t.inclusions?.forEach((i) => all.add(i)));
    return Array.from(all).sort();
  }, [allTours]);

  const companyOptions = useMemo(() => {
    const names = new Set<string>();
    allTours.forEach((t) => {
      if (t.company_name) names.add(t.company_name);
    });
    return Array.from(names).sort();
  }, [allTours]);

  // Dynamic filter options derived from actual tour data
  const dynamicBoatTypes = useMemo(() => deriveOptions(allTours, 'boat_type'), [allTours]);
  const dynamicCategories = useMemo(() => deriveOptions(allTours, 'categories'), [allTours]);
  const dynamicTimeOfDay = useMemo(() => deriveOptions(allTours, 'time_of_day'), [allTours]);
  const dynamicDeparturePoints = useMemo(() => deriveOptions(allTours, 'departure_point'), [allTours]);
  const dynamicPickUp = useMemo(() => deriveOptions(allTours, 'pick_up').map(v => v === 'Not included' ? 'Not available' : v), [allTours]);

  const priceRange = useMemo(() => {
    const prices = allTours.map((t) => getLowestPrice(t)).filter((p): p is number => p !== null);
    if (prices.length === 0) return { min: 0, max: 500 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [allTours]);

  const activeCount = Object.entries(filters).reduce((count, [_, val]) => {
    if (Array.isArray(val)) return count + val.length;
    if (typeof val === "boolean") return count + (val ? 1 : 0);
    return count + (val !== null ? 1 : 0);
  }, 0);

  const checkScroll = () => {
    const el = bodyScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    setCanScrollRight(!atEnd);
    setShowLeftFade(el.scrollLeft > 0 && atEnd);
    if (mobile) {
      setCurrentTourIdx(Math.round(el.scrollLeft / CARD_STEP));
    }
  };

  const scroll = (dir: "left" | "right") => {
    const base = bodyScrollRef.current;
    if (!base) return;
    const currentIndex = Math.round(base.scrollLeft / CARD_STEP);
    const nextIndex = dir === "right" ? currentIndex + 1 : Math.max(0, currentIndex - 1);
    const target = nextIndex * CARD_STEP;
    base.scrollTo({ left: target, behavior: "smooth" });
    setTimeout(checkScroll, 420);
  };

  const syncScroll = (source: "header" | "body") => {
    if (isSyncingScrollRef.current) return;
    const sourceEl = source === "header" ? headerScrollRef.current : bodyScrollRef.current;
    const targetEl = source === "header" ? bodyScrollRef.current : headerScrollRef.current;
    if (!sourceEl || !targetEl) return;

    isSyncingScrollRef.current = true;
    targetEl.scrollLeft = sourceEl.scrollLeft;
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
      checkScroll();
    });
  };

  // Detect when photo row scrolls out of view — use scroll listener for iOS reliability
  useEffect(() => {
    if (!mobile) return;
    const el = photoRowRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      // Show mini-header only when photo row bottom is above viewport
      // AND we've scrolled enough (prevents iOS overscroll bounce from triggering it)
      setShowStickyHeader(rect.bottom < 0 && window.scrollY > 100);
    };

    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [mobile]);

  const handleBodyRef = (el: HTMLDivElement | null) => {
    (bodyScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (el) {
      checkScroll();
      new ResizeObserver(checkScroll).observe(el);
    }
  };

  const handleHeaderRef = (el: HTMLDivElement | null) => {
    (headerScrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };

  if (tours.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground font-display text-lg">No tours match your filters.</p>
        {activeCount > 0 && (
          <button
            onClick={() => handleFiltersChange(emptyFilters)}
            className="mt-3 text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            <XIcon className="w-3 h-3" /> Clear all filters
          </button>
        )}
      </div>
    );
  }

  type Section = {
    id: string;
    label: () => React.ReactNode;
    cell: (t: Tour, idx: number) => React.ReactNode;
    isFirst?: boolean;
    isLast?: boolean;
    alignTop?: boolean;
  };

  const priceSv: [number, number] = [filters.price_min ?? priceRange.min, filters.price_max ?? priceRange.max];

  const sections: Section[] = [
    // Photo (first = rounded top)
    {
      id: "photo",
      isFirst: true,
      label: () => (
        <div className="flex flex-col items-start justify-center h-full w-full gap-0.5">
          <span className="font-display text-2xl md:text-3xl font-extrabold text-foreground leading-none">
            {allTours.length}
          </span>
          <span className="text-[10px] md:text-xs font-bold text-muted-foreground leading-tight text-left">
            Boat tours in Aruba
          </span>
          {activeCount > 0 && (
            <>
              <span className="text-xs md:text-sm font-semibold text-foreground mt-1">Filtered to {tours.length}</span>
              <button
                onClick={() => handleFiltersChange(emptyFilters)}
                className="mt-1 text-[10px] md:text-xs font-semibold text-primary border border-primary/30 rounded-full px-3 py-0.5 hover:bg-primary/10 transition-colors"
              >
                Clear filters
              </button>
            </>
          )}
        </div>
      ),
      cell: (t) => (
        <div className="w-full h-full overflow-hidden relative group">
          {t.cover_photo_url ? (
            <img src={t.cover_photo_url} alt={t.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-xs">No photo</span>
            </div>
          )}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              tableRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
            className="absolute top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-full shadow-md hover:bg-primary/90 transition-colors pointer-events-auto"
          >
            Book Now
          </a>
        </div>
      ),
    },
    // Name
    {
      id: "name",
      label: () => null,
      cell: (t) => <Link to={tourUrl(t.id, t.title)} className="font-display font-bold text-sm leading-tight text-center block hover:text-primary transition-colors">{t.title}</Link>,
    },
    // Description
    {
      id: "description",
      label: () => (
        <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">
          Description
        </span>
      ),
      cell: (t) => <p className="text-xs text-foreground text-center line-clamp-4">{t.short_description || "—"}</p>,
    },
    // Itinerary
    {
      id: "itinerary",
      alignTop: true,
      label: () => (
        <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">Itinerary</span>
      ),
      cell: (t) => {
        const items = (t as any).itinerary as string[] | null;
        if (!items || items.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <ul className="text-xs text-foreground space-y-1 w-full text-left list-disc list-inside">
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
      },
    },
    // Price
    {
      id: "bookhere",
      alignTop: true,
      label: () => {
        return (
          <div className="w-full">
            <span id="book-on" className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">
              Price
            </span>
            <p className="text-[10px] text-muted-foreground leading-snug mt-1.5">
              We regularly update prices but it is recommended to check each site for discounts. Some links are
              affiliate links.
            </p>
            <button
              onClick={() => setPriceFilterOpen((prev) => !prev)}
              className="text-[10px] text-primary hover:underline mt-1 block"
            >
              {priceFilterOpen ? "Hide price filter" : "Price filter"}
            </button>
            {priceFilterOpen && (
              <div className="mt-2 space-y-1.5 animate-fade-in">
                <Slider
                  min={priceRange.min}
                  max={priceRange.max}
                  step={5}
                  value={priceSv}
                  onValueChange={([min, max]) =>
                    handleFiltersChange({
                      ...filters,
                      price_min: min <= priceRange.min ? null : min,
                      price_max: max >= priceRange.max ? null : max,
                    })
                  }
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>${priceSv[0]}</span>
                  <span>${priceSv[1]}</span>
                </div>
              </div>
            )}
          </div>
        );
      },
      cell: (t) => {
        const hasViator = t.tour_platforms?.some((p) => p.platform_name === "Viator");
        const platforms = t.tour_platforms || [];
        const cheapestIds = getCheapestPlatformIds(t);
        return (
          <div className="flex flex-col gap-2 w-full">
            {platforms.length > 0 ? (
              <>
                {platforms.map((p) => (
                  <a
                    key={p.id}
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackPlatformClick(p.id)}
                    className={`flex items-center gap-2 text-xs bg-secondary rounded-lg px-3 py-2 hover:bg-accent transition-colors ${cheapestIds.has(p.id) ? 'cheapest-highlight' : ''}`}
                  >
                    <span className={`font-bold text-sm ${cheapestIds.has(p.id) ? 'text-primary' : ''}`}>${Number(p.price)}</span>
                    <span className="font-semibold">{p.platform_name}</span>
                    <div className="flex items-center gap-2 ml-auto">
                      {p.review_rating != null && (
                        <span className="flex items-center gap-0.5 text-warning">
                          <Star className="w-3 h-3 fill-current" />
                          {Number(p.review_rating).toFixed(1)}
                        </span>
                      )}
                      {p.review_count && <span className="text-muted-foreground">({p.review_count})</span>}
                    </div>
                  </a>
                ))}
                {hasViator && (
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    Tripadvisor and Viator are the same company.
                  </p>
                )}
              </>
            ) : (
              <span className="text-muted-foreground text-xs">No platforms</span>
            )}
          </div>
        );
      },
    },
    // Company (filterable)
    {
      id: "company",
      label: () => (
        <FilterableLabel
          label="Company"
          filterKey="company_name"
          options={companyOptions}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) => t.company_name ? <Link to={companyUrl(t.company_name)} className="text-xs text-primary hover:underline">{t.company_name}</Link> : <span className="text-xs text-foreground">—</span>,
    },
    // Available To (filterable)
    {
      id: "available_to",
      label: () => (
        <FilterableLabel
          label="Available To"
          filterKey="available_to"
          options={AVAILABLE_TO_OPTIONS}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) =>
        t.available_to && t.available_to.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-center">
            {t.available_to.map((v) => (
              <span
                key={v}
                className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    // Departure Point (filterable)
    {
      id: "departure_point",
      label: () => (
        <FilterableLabel
          label="Departure"
          filterKey="departure_point"
          options={dynamicDeparturePoints}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) => <span className="text-xs text-foreground">{t.departure_point || "—"}</span>,
    },
    // Pick Up (filterable)
    {
      id: "pick_up",
      label: () => (
        <FilterableLabel
          label="Pick Up"
          filterKey="pick_up"
          options={dynamicPickUp}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) => {
        const display = displayValue(t.pick_up);
        if (!display) return <span className="text-muted-foreground text-xs">—</span>;
        return <span className="text-xs text-foreground">{display}</span>;
      },
    },
    // Boat Type
    {
      id: "boat_type",
      label: () => (
        <FilterableLabel
          label="Boat Type"
          filterKey="boat_type"
          options={dynamicBoatTypes}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) =>
        t.boat_type ? (
          <span className="text-xs font-medium text-foreground">{t.boat_type}</span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    // Category
    {
      id: "categories",
      label: () => (
        <FilterableLabel
          label="Category"
          filterKey="categories"
          options={dynamicCategories}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) =>
        t.categories && t.categories.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-center">
            {t.categories.map((c) => (
              <span
                key={c}
                className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground"
              >
                {c}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    // Time of Day
    {
      id: "time_of_day",
      label: () => (
        <FilterableLabel
          label="Time of Day"
          mobileLabel="Time"
          filterKey="time_of_day"
          options={dynamicTimeOfDay}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t) =>
        (t as any).time_of_day && (t as any).time_of_day.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-center">
            {(t as any).time_of_day.map((v: string) => (
              <span
                key={v}
                className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    // Duration
    {
      id: "duration",
      label: () => (
        <DualSliderLabel
          label="Duration"
          values={[filters.duration_min, filters.duration_max]}
          range={{ min: 1, max: 12 }}
          step={1}
          format={(v) => `${v}h`}
          onChange={(min, max) => handleFiltersChange({ ...filters, duration_min: min, duration_max: max })}
        />
      ),
      cell: (t) =>
        t.duration_hours !== null ? (
          <span className="text-xs font-medium text-foreground">{t.duration_hours < 0 ? 'Variable' : `${t.duration_hours}h`}</span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    // Inclusions
    {
      id: "inclusions",
      alignTop: true,
      label: () => (
        <div className="w-full">
          <FilterableLabel
            label="Inclusions"
            filterKey="inclusions"
            options={relevantInclusions}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
          <button
            onClick={() => setInclusionsExpanded((prev) => !prev)}
            className="text-[10px] text-primary hover:underline mt-0.5 block"
          >
            {inclusionsExpanded ? (
              <>
                <span className="md:hidden">Collapse</span>
                <span className="hidden md:inline">Collapse comparison</span>
              </>
            ) : (
              <>
                <span className="md:hidden">Expand</span>
                <span className="hidden md:inline">Expand comparison</span>
              </>
            )}
          </button>
        </div>
      ),
      cell: (t) => {
        const items = inclusionsExpanded
          ? relevantInclusions
          : relevantInclusions.filter((item) => t.inclusions?.includes(item));
        return (
          <div className="space-y-1 w-full">
            {items.map((item) => {
              const has = t.inclusions?.includes(item);
              return (
                <div key={item} className="flex items-center gap-2 text-xs">
                  {has ? (
                    <Check className="w-3.5 h-3.5 text-success flex-shrink-0" />
                  ) : (
                    <XIcon className="w-3.5 h-3.5 text-destructive/50 flex-shrink-0" />
                  )}
                  <span className={has ? "text-foreground font-medium" : "text-muted-foreground/60 line-through"}>
                    {item}
                  </span>
                </div>
              );
            })}
            {items.length === 0 && <span className="text-muted-foreground text-xs">—</span>}
          </div>
        );
      },
    },
    // Section heading before attributes
    {
      id: "attr-heading",
      label: () => null,
      cell: () => (
        <div className="w-full py-2 text-center bg-primary rounded-lg">
          <p className="text-sm font-bold text-primary-foreground uppercase tracking-wide">What people say</p>
          <p className="text-sm font-bold text-primary-foreground uppercase tracking-wide">in the reviews</p>
        </div>
      ),
    },
    // Attribute rows (text-based: vibe, crowd_size, boarding, best_for)
    ...ATTR_FILTER_CONFIG.map((config) => ({
      id: config.key,
      label: () => (
        <FilterableLabel
          label={config.label}
          mobileLabel={config.mobileLabel}
          filterKey={config.key}
          options={config.options}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      ),
      cell: (t: Tour) => {
        if (config.key === "best_for") {
          const vals = t.best_for;
          if (!vals || vals.length === 0) return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <div className="flex flex-wrap gap-1 justify-center">
              {vals.map((v) => (
                <span
                  key={v}
                  className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${getAttributeColor(v)}`}
                >
                  {v}
                </span>
              ))}
            </div>
          );
        }
        return <AttributeCell value={t[config.key] as string | null} />;
      },
    })),
    // Rating rows (circle display: snorkeling, food, open_bar, crew)
    ...RATING_ATTRS.map((attr) => {
      const baseKey =
        attr.key === "snorkeling_quality"
          ? "snorkeling"
          : attr.key === "food_quality"
            ? "food"
            : attr.key === "open_bar"
              ? "open_bar"
              : "crew";
      const filterKey = `${baseKey}_min` as keyof FilterState;
      const noKey = `${baseKey}_no` as keyof FilterState;
      const noLabel =
        attr.key === "snorkeling_quality"
          ? "No snorkeling"
          : attr.key === "food_quality"
            ? "No food"
            : attr.key === "open_bar"
              ? "No open bar"
              : "No crew";
      return {
        id: attr.key,
        label: () => {
          return (
            <SliderLabel
              label={attr.label}
              value={filters[filterKey] as number | null}
              range={{ min: 1, max: 5 }}
              onChange={(v) => handleFiltersChange({ ...filters, [filterKey]: v, [noKey]: false })}
              noOption={noLabel}
              noActive={filters[noKey] as boolean}
              onNoChange={(checked) =>
                handleFiltersChange({ ...filters, [noKey]: checked, [filterKey]: checked ? null : filters[filterKey] })
              }
            />
          );
        },
        cell: (t: Tour) => {
          const val = t[attr.key] as string | null;
          if (!val) return <CircleRating value={null} />;
          const noLabels = ["No snorkeling", "No food", "No open bar", "No crew"];
          if (noLabels.includes(val)) {
            return <span className="text-xs text-muted-foreground">{val}</span>;
          }
          if (val === "No alcohol") {
            return <span className="text-xs text-muted-foreground">No alcohol</span>;
          }
          // Handle "3|No alcohol" format
          if (val.includes("|No alcohol")) {
            const rating = val.split("|")[0];
            return <CircleRating value={rating} extra="No alcohol" />;
          }
          return <CircleRating value={val} />;
        },
      };
    }),
    // What People Say
    {
      id: "whatpeoplesay",
      alignTop: true,
      label: () => (
        <div className="w-full">
          <span className="text-[10px] md:text-xs font-semibold text-foreground uppercase tracking-wide">Reviews</span>
          <p className="text-[10px] text-muted-foreground leading-snug mt-1.5">
            This is a summary of real traveler reviews. To read the full reviews, click on one of the{" "}
            <a
              href="#book-on"
              onClick={(e) => {
                e.preventDefault();
                tableRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
              className="text-primary underline hover:text-primary/80 transition-colors"
            >
              booking links
            </a>
            .
          </p>
        </div>
      ),
      cell: (t) => (
        <div className="space-y-1.5 w-full">
          {t.pros?.map((p, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <Check className="w-3.5 h-3.5 text-success flex-shrink-0 mt-0.5" />
              <span className="text-foreground font-medium">{p}</span>
            </div>
          ))}
          {t.cons?.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0 mt-0.5" />
              <span className="text-foreground font-medium">{c}</span>
            </div>
          ))}
          {(!t.pros || t.pros.length === 0) && (!t.cons || t.cons.length === 0) && (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      ),
    },
    // Book Now (last = rounded bottom)
    {
      id: "book-now-bottom",
      isLast: true,
      label: () => null,
      cell: (t) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            tableRef.current?.scrollIntoView({ behavior: "smooth" });
          }}
          className="w-full block text-center text-sm font-semibold py-2.5 rounded-lg border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground active:bg-primary/80 active:text-primary-foreground transition-colors"
        >
          Book Now
        </a>
      ),
    },
  ];

  // Split sections into sticky (photo + name) and the rest
  const stickySections = sections.filter((s) => s.id === "photo" || s.id === "name");
  const restSections = sections.filter((s) => s.id !== "photo" && s.id !== "name");

  const handleCardHover = (tourId: string, hover: boolean) => {
    document.querySelectorAll(`[data-tour-id="${tourId}"] > div`).forEach((el) => {
      if (hover) {
        el.classList.add("shadow-lg");
      } else {
        el.classList.remove("shadow-lg");
      }
    });
  };

  if (mobile) {
    const currentTour = tours[currentTourIdx] || tours[0];

    return (
      <div ref={tableRef} className="relative">
        {/* Sticky mini-header: appears when photo+name scroll out of view */}
        {showStickyHeader && currentTour && (
          <div className="fixed top-0 left-0 right-0 z-40 bg-card border-b border-border shadow-sm">
            <div className="flex items-center gap-2 px-3 py-2">
              {currentTour.cover_photo_url && (
                <img
                  src={currentTour.cover_photo_url}
                  alt={currentTour.title}
                  className="rounded-md object-cover flex-shrink-0"
                  style={{ width: 60, height: 38 }}
                />
              )}
              <div className="flex flex-col min-w-0 gap-[1px]">
                <span className="font-display font-bold text-sm leading-tight truncate">{currentTour.title}</span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    tableRef.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-xs text-primary font-semibold hover:underline"
                >
                  Book Now
                </a>
              </div>
              <div className="ml-auto flex flex-col items-center gap-0.5">
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={!canScrollLeft}
                    className="rounded-full h-7 w-7 border-foreground/30 disabled:opacity-50"
                    onClick={() => scroll("left")}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={!canScrollRight}
                    className="rounded-full h-7 w-7 border-foreground/30 disabled:opacity-50"
                    onClick={() => scroll("right")}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <span className="text-[10px] text-muted-foreground font-medium">
                  {currentTourIdx + 1}/{tours.length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation arrows over the photo area (visible when photo is in view) */}
        {!showStickyHeader && (
          <div className="sticky top-0 z-50 pointer-events-none" style={{ height: 0 }}>
            <div className="relative">
              <div className="h-[10px]" />
              <div
                className="absolute top-[80px] -translate-y-1/2 z-50 flex flex-col items-center"
                style={{ left: LABEL_W_PX - 14 }}
              >
                <Button
                  variant="outline"
                  size="icon"
                  disabled={!canScrollLeft}
                  className="pointer-events-auto rounded-full shadow-lg bg-card border-foreground/30 h-8 w-8 disabled:opacity-50"
                  onClick={() => scroll("left")}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </div>
              <div className="absolute top-[80px] -translate-y-1/2 z-50 right-[5px] flex flex-col items-center">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={!canScrollRight}
                  className="pointer-events-auto rounded-full shadow-lg bg-card border-foreground/30 h-8 w-8 disabled:opacity-50"
                  onClick={() => scroll("right")}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <div
          ref={handleBodyRef}
          onScroll={checkScroll}
          className="overflow-x-auto pb-[20px] snap-x snap-mandatory"
          style={{ scrollPaddingLeft: LABEL_W_PX, WebkitOverflowScrolling: "touch" } as React.CSSProperties}
        >
          <div className="inline-flex flex-col min-w-full">
            {sections.map((section) => (
              <div
                key={section.id}
                ref={section.id === "photo" ? photoRowRef : undefined}
                className={`flex ${section.id === "photo" ? "items-start" : "items-stretch"}`}
              >
                <LabelCell className={section.id === "photo" ? "aspect-video" : ""} labelW={LABEL_W_PX}>
                  {section.label()}
                </LabelCell>
                {tours.map((t, tIdx) => (
                  <div
                    key={t.id}
                    data-tour-id={t.id}
                    className="tour-card-col flex-shrink-0 px-1 flex snap-start"
                    style={{ minWidth: CARD_W_PX, width: CARD_W_PX }}
                    onMouseEnter={() => handleCardHover(t.id, true)}
                    onMouseLeave={() => handleCardHover(t.id, false)}
                  >
                    <div
                      className={`
                        bg-card border-x border-border w-full transition-shadow
                        ${section.isFirst ? "border-t rounded-t-xl overflow-hidden" : ""}
                        ${
                          section.id === "photo"
                            ? "aspect-video"
                            : section.isLast
                              ? "border-b rounded-b-xl pb-[20px] px-3 py-3"
                              : "border-b px-3 py-3"
                        }
                        flex items-start justify-center
                      `}
                    >
                      {section.cell(t, tIdx)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={tableRef} className="relative">
      {/* Navigation arrows - positioned over the sticky area */}
      <div className="sticky top-0 z-50 pointer-events-none" style={{ height: 0 }}>
        <div className="relative">
          <div className="h-[10px]" />
          <Button
            variant="outline"
            size="icon"
            disabled={!canScrollLeft}
            className="pointer-events-auto absolute top-[80px] md:top-[100px] -translate-y-1/2 z-50 rounded-full shadow-lg bg-card border-foreground/30 h-8 w-8 md:h-10 md:w-10 disabled:opacity-50"
            style={{ left: LABEL_W_PX - 14 }}
            onClick={() => scroll("left")}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!canScrollRight}
            className="pointer-events-auto absolute top-[80px] md:top-[100px] -translate-y-1/2 z-50 rounded-full shadow-lg bg-card border-foreground/30 h-8 w-8 md:h-10 md:w-10 disabled:opacity-50 right-[5px] md:right-[-16px]"
            onClick={() => scroll("right")}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Sticky header rows (photo + name) */}
      <div className="sticky top-0 z-30">
        <div className="h-[10px] bg-background" />
        <div className="relative">
          <div
            ref={handleHeaderRef}
            onScroll={() => syncScroll("header")}
            className="overflow-x-auto overflow-y-visible bg-background shadow-sm"
            style={{ scrollPaddingLeft: LABEL_W_PX, scrollbarWidth: "none", msOverflowStyle: "none" as const }}
          >
            <div className="inline-flex flex-col min-w-full">
              {stickySections.map((section) => (
                <div key={section.id} className={`flex ${section.id === "photo" ? "items-start" : "items-stretch"}`}>
                  <LabelCell className={section.id === "photo" ? "aspect-video" : ""} labelW={LABEL_W_PX}>
                    {section.label()}
                  </LabelCell>
                  {tours.map((t, tIdx) => (
                    <div
                      key={t.id}
                      data-tour-id={t.id}
                      className="tour-card-col flex-shrink-0 px-1 md:px-2 flex snap-start"
                      style={{ minWidth: CARD_W_PX, width: CARD_W_PX }}
                      onMouseEnter={() => handleCardHover(t.id, true)}
                      onMouseLeave={() => handleCardHover(t.id, false)}
                    >
                      <div
                        className={`
                          bg-card border-x border-border w-full transition-shadow
                          ${section.isFirst ? "border-t rounded-t-xl overflow-hidden" : ""}
                          ${section.id === "photo" ? "aspect-video" : "px-3 md:px-5 py-3 border-b"}
                          flex items-start justify-center
                        `}
                      >
                        {section.cell(t, tIdx)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {/* Fade edges – positioned outside the scrolling div so they stay fixed */}
          {showLeftFade && (
            <div
              className="pointer-events-none absolute top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-background to-transparent hidden md:block"
              style={{ left: `${LABEL_W_PX}px` }}
            />
          )}
          {canScrollRight && (
            <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-16 z-10 bg-gradient-to-l from-background to-transparent hidden md:block" />
          )}
        </div>
      </div>

      {/* Body scroll container */}
      <div
        ref={handleBodyRef}
        onScroll={() => syncScroll("body")}
        className="overflow-x-auto pb-[20px] snap-x snap-mandatory"
        style={{ scrollPaddingLeft: LABEL_W_PX, WebkitOverflowScrolling: "touch" } as React.CSSProperties}
      >
        <div className="inline-flex flex-col min-w-full">
          {restSections.map((section) => (
            <div key={section.id} className="flex items-stretch">
              <LabelCell labelW={LABEL_W_PX}>{section.label()}</LabelCell>
              {tours.map((t, tIdx) => (
                <div
                  key={t.id}
                  data-tour-id={t.id}
                  className="tour-card-col flex-shrink-0 px-1 md:px-2 flex snap-start"
                  style={{ minWidth: CARD_W_PX, width: CARD_W_PX }}
                  onMouseEnter={() => handleCardHover(t.id, true)}
                  onMouseLeave={() => handleCardHover(t.id, false)}
                >
                  <div
                    className={`
                    bg-card border-x border-border w-full transition-shadow
                    ${section.isLast ? "border-b rounded-b-xl pb-[20px]" : "border-b"}
                    px-3 md:px-5 py-3 flex items-start justify-center
                  `}
                  >
                    {section.cell(t, tIdx)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Persistent fade edges overlaying the entire scroll area */}
      {showLeftFade && (
        <div
          className="pointer-events-none absolute top-0 bottom-0 w-16 z-20 bg-gradient-to-r from-background to-transparent hidden md:block"
          style={{ left: `${LABEL_W_PX}px` }}
        />
      )}
      {canScrollRight && (
        <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-16 z-20 bg-gradient-to-l from-background to-transparent hidden md:block" />
      )}
    </div>
  );
}
